import { apiClient } from './apiClient';
import { handleResponse, extractApiErrorMessage } from '../utils/apiUtils';
import { cacheService } from './cacheService';
import { indexedDbService, ChangeType, PendingChange } from './indexedDbService';
import { offlineService } from './offlineService';
import { v4 as uuidv4 } from 'uuid';
import { networkService } from './appointmentExceptionService';
import { appointmentExceptionService, AppointmentException } from './appointmentExceptionService';
import { RecurrenceGenerator } from './recurrencePatterns/RecurrenceGenerator';
import { apiHealthService } from './apiHealthService';

export interface Appointment {
    id: string;
    recurringAppointmentId?: string;  // Parent appointment ID for recurring instances
    title: string;
    description: string;
    appointmentTypeId: string;
    startTime: string;
    endTime: string;
    isAllDay: boolean;
    recurrenceRule?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    timeZoneId: string;
    userId?: string;
    businessId: string;
    hasWriteAccess: boolean;
    createdAt: string;  // ISO date string for appointment creation time
    createdByUserName?: string;  // Creator's full name
    createdByUserEmail?: string;  // Creator's email address
    isException?: boolean;  // Whether this is an exception to a recurring appointment
    exceptionDate?: string;  // The date of the exception (ISO date string)
    isDeleted?: boolean;  // Whether this appointment has been deleted (for offline mode)
    updatedAt: string;  // ISO date string for appointment update time
    isRecurringInstance?: boolean;  // Whether this is a generated instance of a recurring appointment
    customerId?: string;  // Optional link to Customer
    customerName?: string;  // Customer's full name
    customerEmail?: string;  // Customer's email
    customerPhone?: string;  // Customer's phone number
    jobId?: string;  // Optional link to Job
    jobNumber?: string;  // Job number (e.g., KE-126)
    originalStartTime?: string;  // Original recurring appointment start time
    originalEndTime?: string;  // Original recurring appointment end time
}

export interface AppointmentResult {
    success: boolean;
    errorMessage?: string;
    appointment?: Appointment;
}

export interface GetAppointmentsParams {
    startDate?: string;
    endDate?: string;
    appointmentTypeIds?: string[];
    targetUserId?: string; // ID of the user whose appointments to retrieve
    _forceOffline?: boolean; // Flag to force offline mode for testing or fallback
}

export interface CreateAppointmentDto {
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    isAllDay: boolean;
    recurrenceRule?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    appointmentTypeId: string;
    timeZoneId: string;
    userId?: string;
    customerId?: string;
    jobId?: string;
}

export interface UpdateAppointmentDto {
    id: string;
    title: string;
    description: string;
    appointmentTypeId: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    userId?: string;
    startTime: string;
    endTime: string;
    isAllDay: boolean;
    timeZoneId: string;
    recurrenceRule?: string;
    originalDate?: Date;  // Add this field for recurring instance updates
    customerId?: string;
    jobId?: string;
}

class AppointmentService {
    private static instance: AppointmentService;
    private readonly baseUrl: string;
    // Cache expiration time: 2 minutes for appointments (shorter because they change frequently)
    private readonly CACHE_EXPIRATION = 2 * 60 * 1000;
    // Cache expiration time for recent activity: 1 minute
    private readonly RECENT_ACTIVITY_CACHE_EXPIRATION = 1 * 60 * 1000;

    private constructor() {
        this.baseUrl = `${apiHealthService.getApiUrl()}/api/appointments`;
    }

    public static getInstance(): AppointmentService {
        if (!AppointmentService.instance) {
            AppointmentService.instance = new AppointmentService();
        }
        return AppointmentService.instance;
    }

    private buildQueryString(params: GetAppointmentsParams): string {
        const queryParams = new URLSearchParams();
        
        // Add start date if provided
        if (params.startDate) {
            queryParams.append('start', params.startDate);
        }
        
        // Add end date if provided
        if (params.endDate) {
            queryParams.append('end', params.endDate);
        }
        
        // Add appointment type IDs if provided
        if (params.appointmentTypeIds && params.appointmentTypeIds.length > 0) {
            params.appointmentTypeIds.forEach(id => {
                queryParams.append('appointmentTypeIds', id);
            });
        }
        
        // Add target user ID if provided
        if (params.targetUserId) {
            queryParams.append('targetUserId', params.targetUserId);
        }
        
        // Note: We intentionally exclude _forceOffline as it's only used client-side
        
        const queryString = queryParams.toString();
        return queryString ? `?${queryString}` : '';
    }

    /**
     * Check if the API is available
     * @returns A promise that resolves to true if the API is available, false otherwise
     */
    private async isApiAvailable(): Promise<boolean> {
        return apiHealthService.isAvailable();
    }

    async getAppointments(params: GetAppointmentsParams = {}): Promise<Appointment[]> {
        const queryString = this.buildQueryString(params);
        // Create a cache key based on the query parameters
        const cacheKey = `appointments:${queryString}`;
        
        // Check if we're offline or if offline mode is forced
        const isOffline = !offlineService.getOnlineStatus() || params._forceOffline;
        
        if (isOffline) {
            console.log(`${params._forceOffline ? 'Forced offline mode' : 'Offline mode'}: Getting appointments from IndexedDB`);
            try {
                // Convert string dates to Date objects for the recurrence generator
                const startDate = params.startDate ? new Date(params.startDate) : new Date();
                const endDate = params.endDate ? new Date(params.endDate) : new Date();
                
                // Set default date range if not provided
                if (!params.startDate) {
                    startDate.setMonth(startDate.getMonth() - 1); // 1 month ago
                }
                if (!params.endDate) {
                    endDate.setMonth(endDate.getMonth() + 6); // 6 months in the future
                }
                
                // Get the current user's business ID
                const currentUser = await indexedDbService.getCurrentUser();
                if (!currentUser) {
                    console.error('No current user found in offline mode');
                    return [];
                }
                
                const businessId = currentUser.businessId;
                console.log(`Getting appointments for business ${businessId} from IndexedDB`);
                
                return this.getAppointmentsFromIndexedDb(businessId, startDate, endDate);
            } catch (error) {
                console.error('Error fetching appointments from IndexedDB:', error);
                return [];
            }
        }
        
        // Check if we have a cached version
        const cachedData = cacheService.get<Appointment[]>(cacheKey);
        if (cachedData) {
            console.log(`Using cached appointments for ${queryString}`);
            return cachedData;
        }
        
        // No cache, make the API call
        try {
            console.log(`Making API call to: ${this.baseUrl}${queryString}`);
            
            // Use apiClient instead of fetch to ensure authentication headers are included
            const response = await apiClient.get(`/appointments${queryString}`);
            
            const appointments = response.data;
            console.log(`Received ${appointments.length} appointments from API`);
            
            // Log the structure of the appointments
            if (appointments.length > 0) {
                // Count appointments by type
                const baseRecurring = appointments.filter((a: Appointment) => a.recurrenceRule).length;
                const nonRecurring = appointments.filter((a: Appointment) => !a.recurrenceRule && !a.recurringAppointmentId).length;
                const exceptions = appointments.filter((a: Appointment) => a.isException).length;
                const instances = appointments.filter((a: Appointment) => a.recurringAppointmentId && !a.isException).length;
                
                console.log('Appointment counts by type:', {
                    baseRecurring,
                    nonRecurring,
                    exceptions,
                    instances
                });
            }
            
            // Cache the result
            cacheService.set(cacheKey, appointments, this.CACHE_EXPIRATION);
            
            // Note: IndexedDB caching is now handled by the caller (MainCalendar, Dashboard)
            // using mergeCachedAppointments() to preserve data outside the fetched range
            
            return appointments;
        } catch (error) {
            console.error(`Error fetching appointments: ${error}`);
            
            // Try to get from IndexedDB as fallback
            try {
                console.log('Falling back to IndexedDB for appointments');
                
                // Convert string dates to Date objects for the recurrence generator
                const startDate = params.startDate ? new Date(params.startDate) : new Date();
                const endDate = params.endDate ? new Date(params.endDate) : new Date();
                
                // Set default date range if not provided
                if (!params.startDate) {
                    startDate.setMonth(startDate.getMonth() - 1); // 1 month ago
                }
                if (!params.endDate) {
                    endDate.setMonth(endDate.getMonth() + 6); // 6 months in the future
                }
                
                // Get the current user's business ID
                const currentUser = await indexedDbService.getCurrentUser();
                if (!currentUser) {
                    console.error('No current user found in offline mode');
                    return [];
                }
                
                const businessId = currentUser.businessId;
                
                return this.getAppointmentsFromIndexedDb(businessId, startDate, endDate);
            } catch (dbError) {
                console.error('IndexedDB fallback failed:', dbError);
                return [];
            }
        }
    }

    async getRecentActivity(): Promise<Appointment[]> {
        const cacheKey = 'appointments:recent-activity';
        
        return cacheService.getOrSet(
            cacheKey,
            async () => {
                try {
                    console.log('Fetching recent activity');
                    const response = await apiClient.get<Appointment[]>(`${this.baseUrl}/recent-activity`);
                    console.log('Recent activity response:', response.data);
                    return response.data;
                } catch (error) {
                    console.error('Error fetching recent activity:', error);
                    throw error;
                }
            },
            this.RECENT_ACTIVITY_CACHE_EXPIRATION
        );
    }

    async getAppointmentsByJobId(jobId: string): Promise<Appointment[]> {
        try {
            const response = await apiClient.get<Appointment[]>(`${this.baseUrl}/by-job/${jobId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching appointments for job:', error);
            throw error;
        }
    }

    async getAppointmentsByCustomerId(customerId: string): Promise<Appointment[]> {
        try {
            console.log('Fetching appointments for customer:', customerId);
            const response = await apiClient.get<Appointment[]>(`${this.baseUrl}/by-customer/${customerId}`);
            console.log('Customer appointments response:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching appointments for customer:', error);
            throw error;
        }
    }

    async getAppointment(id: string): Promise<Appointment> {
        const cacheKey = `appointment:${id}`;
        
        // Check if we're offline
        const isOffline = !offlineService.getOnlineStatus();
        
        if (isOffline) {
            console.log(`Offline mode: Getting appointment ${id} from IndexedDB`);
            try {
                const appointment = await indexedDbService.getAppointment(id);
                if (appointment) {
                    return appointment;
                }
                throw new Error(`Appointment ${id} not found in offline storage`);
            } catch (error) {
                console.error(`Error getting appointment ${id} from IndexedDB:`, error);
                throw error;
            }
        }
        
        return cacheService.getOrSet(
            cacheKey,
            async () => {
                try {
                    const response = await apiClient.get<Appointment>(`${this.baseUrl}/${id}`);
                    
                    // Store in IndexedDB for offline use
                    try {
                        await indexedDbService.updateAppointment(response.data);
                    } catch (dbError) {
                        console.error(`Error saving appointment ${id} to IndexedDB:`, dbError);
                    }
                    
                    return response.data;
                } catch (error) {
                    console.error('Error fetching appointment:', error);
                    
                    // If API request fails, try to get from IndexedDB
                    try {
                        const offlineAppointment = await indexedDbService.getAppointment(id);
                        if (offlineAppointment) {
                            return offlineAppointment;
                        }
                    } catch (dbError) {
                        console.error(`Error getting appointment ${id} from IndexedDB:`, dbError);
                    }
                    
                    throw error; // Re-throw the original error
                }
            },
            this.CACHE_EXPIRATION
        );
    }

    async createAppointment(appointment: CreateAppointmentDto): Promise<AppointmentResult> {
        try {
            const formattedAppointment = {
                ...appointment,
                startTime: appointment.startTime,
                endTime: appointment.endTime,
                timeZoneId: appointment.timeZoneId || 'UTC'
            };

            if (!formattedAppointment.appointmentTypeId) throw new Error('AppointmentTypeId is required');
            
            const guidRegex = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/;
            if (!guidRegex.test(formattedAppointment.appointmentTypeId)) {
                throw new Error('Invalid AppointmentTypeId format. Must be a valid GUID.');
            }

            const response = await apiClient.post<AppointmentResult>(this.baseUrl, formattedAppointment);
            
            // Invalidate appointment caches
            this.invalidateAppointmentCaches();
            
            return response.data;
        } catch (error: any) {
            console.error('Error creating appointment:', error);
            return {
                success: false,
                errorMessage: extractApiErrorMessage(error, 'An error occurred while creating the appointment')
            };
        }
    }

    /**
     * Updates an appointment
     * @param appointment The appointment DTO with updated values (must include id field)
     * @returns Promise with the result of the update operation
     */
    async updateAppointment(appointment: UpdateAppointmentDto): Promise<AppointmentResult> {
        try {
            // Validate that appointment has an ID
            if (!appointment.id) {
                console.error('updateAppointment called with missing id field in DTO');
                return {
                    success: false,
                    errorMessage: 'Appointment id is required'
                };
            }

            const id = appointment.id;
            
            // Check if we're offline - use both the service and the browser's status
            const serviceOfflineStatus = !offlineService.getOnlineStatus();
            const browserOfflineStatus = !navigator.onLine;
            const isOffline = serviceOfflineStatus || browserOfflineStatus;
            
            console.log('updateAppointment - Offline detection:', {
                serviceOfflineStatus,
                browserOfflineStatus,
                isOffline
            });
            
            // Get the current timezone
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            // Ensure timezone is set
            if (!appointment.timeZoneId) {
                appointment.timeZoneId = timeZone;
                console.log('Setting missing timeZoneId in updateAppointment:', timeZone);
            }
            
            // Log appointment times for debugging
            console.log('Appointment times in updateAppointment:', {
                id: appointment.id,
                startTime: appointment.startTime,
                endTime: appointment.endTime,
                timeZoneId: appointment.timeZoneId
            });
            
            if (isOffline) {
                console.log('Handling appointment update in offline mode');
                
                // In offline mode, we'll update the appointment in IndexedDB
                // and add a pending change to be synced when online
                
                // First, get the appointment from IndexedDB
                const existingAppointment = await indexedDbService.getAppointment(appointment.id);
                
                if (existingAppointment) {
                    // Update the appointment in IndexedDB
                    const updatedAppointment = {
                        ...existingAppointment,
                        ...appointment,
                        timeZoneId: appointment.timeZoneId || timeZone // Ensure timezone is set
                    };
                    
                    await indexedDbService.updateAppointment(updatedAppointment);
                    
                    // Use the API client to update the appointment
                    // This will be intercepted in offline mode and added to pending changes
                    try {
                        console.log('Making API call to update appointment (will be handled by offline interceptor)');
                        await apiClient.put(`${this.baseUrl}/${appointment.id}`, appointment);
                    } catch (error: any) {
                        // This error is expected in offline mode
                        if (error.isOfflineError) {
                            console.log('Offline error caught as expected, update request queued for processing when online');
                        } else {
                            console.error('Unexpected error making API call for update:', error);
                        }
                    }
                    
                    console.log('Added pending change for appointment update');
                    
                    // Return a mock successful response
                    return {
                        success: true,
                        appointment: updatedAppointment
                    };
                } else {
                    console.error('Appointment not found in IndexedDB:', id);
                    return {
                        success: false,
                        errorMessage: 'Appointment not found in offline storage'
                    };
                }
            }
            
            // Online mode - make the API call
            const response = await apiClient.put<AppointmentResult>(`${this.baseUrl}/${appointment.id}`, appointment);
            
            // Ensure the response contains the ID
            if (response.data.appointment && !response.data.appointment.id) {
                console.log('API response missing appointment ID, setting it manually');
                response.data.appointment.id = appointment.id;
            }
            
            // Invalidate appointment caches
            this.invalidateAppointmentCaches();
            cacheService.remove(`appointment:${appointment.id}`);
            
            // Check if the response needs to be wrapped (may come directly as Appointment instead of AppointmentResult)
            if (response.data && typeof response.data.success === 'undefined') {
                // Direct appointment object, wrap it
                return {
                    success: true,
                    appointment: response.data as unknown as Appointment
                };
            }
            
            return response.data;
        } catch (error: any) {
            // Check if this is our custom offline error
            if (error.isOfflineError) {
                console.log('Offline error caught in updateAppointment');
                // Return a mock successful response with ID explicitly set
                return {
                    success: true,
                    appointment: {
                        ...appointment
                    } as unknown as Appointment
                };
            }

            console.error('Error updating appointment:', error);
            return {
                success: false,
                errorMessage: extractApiErrorMessage(error, 'An error occurred while updating the appointment')
            };
        }
    }

    async updateRecurringSeries(id: string, appointment: UpdateAppointmentDto, updateOption: 'ThisInstance' | 'AllInstances'): Promise<AppointmentResult> {
        try {
            const response = await apiClient.put<AppointmentResult>(`${this.baseUrl}/recurring/series/${id}`, {
                ...appointment,
                updateOption
            });
            
            // Invalidate appointment caches
            this.invalidateAppointmentCaches();
            
            return response.data;
        } catch (error) {
            console.error('Error updating recurring appointment:', error);
            throw error;
        }
    }

    async updateRecurringInstance(id: string, appointment: UpdateAppointmentDto): Promise<AppointmentResult> {
        try {
            // Check if we're offline - use both the service and the browser's status
            const serviceOfflineStatus = !offlineService.getOnlineStatus();
            const browserOfflineStatus = !navigator.onLine;
            const isOffline = serviceOfflineStatus || browserOfflineStatus;
            
            console.log('updateRecurringInstance - Offline detection:', {
                serviceOfflineStatus,
                browserOfflineStatus,
                isOffline
            });
            
            // Get the current timezone
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            // Ensure timezone is set
            if (!appointment.timeZoneId) {
                appointment.timeZoneId = timeZone;
                console.log('Setting missing timeZoneId in updateRecurringInstance:', timeZone);
            }
            
            // Ensure consistent date handling for originalDate
            if (appointment.originalDate) {
                // Get timezone from the appointment
                const timeZoneId = appointment.timeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;
                
                // Use the original appointment's startTime to ensure we reference the correct instance
                const originalDate = appointment.originalDate;
                
                // Format the date in the appointment's timezone to ensure correct date
                const dateFormatter = new Intl.DateTimeFormat('en-GB', {
                    timeZone: timeZoneId,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
                
                // Get the date part in the appointment's timezone
                const originalDateFormatted = dateFormatter.format(originalDate);
                
                // Parse the formatted date and create a UTC midnight date
                const dateParts = originalDateFormatted.split('/');
                const normalizedDate = new Date(Date.UTC(
                    parseInt(dateParts[2]), // year
                    parseInt(dateParts[1]) - 1, // month (0-based)
                    parseInt(dateParts[0]) // day
                ));
                
                console.log('Processing originalDate in updateRecurringInstance:', {
                    originalDate,
                    originalDateISO: originalDate.toISOString(),
                    originalDateFormatted,
                    normalizedDate: normalizedDate.toISOString(),
                    timeZoneId
                });
                
                // Use the normalized date to ensure proper date comparison on the server
                appointment.originalDate = normalizedDate;
            }
            
            // Ensure start and end times are in UTC format with Z suffix
            if (typeof appointment.startTime === 'string' && !appointment.startTime.endsWith('Z')) {
                appointment.startTime = `${appointment.startTime}Z`;
                console.log(`Corrected startTime to UTC format: ${appointment.startTime}`);
            }
            
            if (typeof appointment.endTime === 'string' && !appointment.endTime.endsWith('Z')) {
                appointment.endTime = `${appointment.endTime}Z`;
                console.log(`Corrected endTime to UTC format: ${appointment.endTime}`);
            }
            
            // Log appointment times for debugging
            console.log('Appointment times in updateRecurringInstance:', {
                startTime: appointment.startTime,
                endTime: appointment.endTime,
                timeZoneId: appointment.timeZoneId,
                originalDate: appointment.originalDate
            });
            
            if (isOffline) {
                console.log('Handling recurring instance update in offline mode');
                
                // In offline mode, we need to:
                // 1. Create an exception for this instance
                // 2. Add a pending change to be synced when online
                
                // First, get the base recurring appointment from IndexedDB
                const baseAppointment = await indexedDbService.getAppointment(id);
                
                if (baseAppointment) {
                    console.log('Found base recurring appointment:', {
                        id: baseAppointment.id,
                        title: baseAppointment.title,
                        recurrenceRule: baseAppointment.recurrenceRule
                    });
                    
                    // Create an exception for this instance
                    if (appointment.originalDate) {
                        const exceptionDate = appointment.originalDate.toISOString().split('T')[0];
                        console.log(`Creating exception for date ${exceptionDate}`, {
                            originalDate: appointment.originalDate,
                            exceptionDate: exceptionDate,
                            startTime: appointment.startTime,
                            endTime: appointment.endTime
                        });
                        
                        try {
                            // Prepare the start and end times
                            let newStartTime: string;
                            let newEndTime: string;
                            
                            if (typeof appointment.startTime === 'string') {
                                newStartTime = appointment.startTime;
                            } else {
                                newStartTime = (appointment.startTime as Date).toISOString();
                            }
                            
                            if (typeof appointment.endTime === 'string') {
                                newEndTime = appointment.endTime;
                            } else {
                                newEndTime = (appointment.endTime as Date).toISOString();
                            }
                            
                            // Ensure the date part of newStartTime matches the exceptionDate
                            // This is critical to prevent the 24-hour offset issue
                            const startTimeDate = new Date(newStartTime);
                            const exceptionDateObj = new Date(`${exceptionDate}T00:00:00Z`);
                            
                            console.log('Checking date alignment:', {
                                originalStartTime: newStartTime,
                                startTimeDate: startTimeDate.toISOString(),
                                exceptionDate: exceptionDate,
                                exceptionDateObj: exceptionDateObj.toISOString()
                            });
                            
                            // If the dates don't match, adjust the time to use the exception date
                            if (startTimeDate.toISOString().split('T')[0] !== exceptionDate) {
                                console.warn(`Fixing date mismatch: ${startTimeDate.toISOString().split('T')[0]} vs ${exceptionDate}`);
                                
                                // Extract the time part from the original start time
                                const timePart = startTimeDate.toISOString().split('T')[1];
                                
                                // Combine the exception date with the original time
                                newStartTime = `${exceptionDate}T${timePart}`;
                                console.log(`Adjusted newStartTime to: ${newStartTime}`);
                                
                                // Also adjust the end time to maintain the same duration
                                const endTimeDate = new Date(newEndTime);
                                const durationMs = endTimeDate.getTime() - startTimeDate.getTime();
                                const newEndTimeDate = new Date(new Date(newStartTime).getTime() + durationMs);
                                newEndTime = newEndTimeDate.toISOString();
                                console.log(`Adjusted newEndTime to: ${newEndTime}`);
                            }
                            
                            // Create the exception
                            const exception: AppointmentException = {
                                id: `${id}_${exceptionDate}`,
                                appointmentId: id,
                                exceptionDate: exceptionDate,
                                isDeleted: false,
                                newStartTime: this.ensureUTCFormat(newStartTime),
                                newEndTime: this.ensureUTCFormat(newEndTime),
                                newTitle: appointment.title,
                                newDescription: appointment.description,
                                newAddress: appointment.address,
                                newIsAllDay: appointment.isAllDay,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                            };
                            
                            console.log('Created exception object:', {
                                id: exception.id,
                                appointmentId: exception.appointmentId,
                                exceptionDate: exception.exceptionDate,
                                isDeleted: exception.isDeleted,
                                newStartTime: exception.newStartTime,
                                newEndTime: exception.newEndTime
                            });
                            
                            // Check if the newStartTime has the same date as the exceptionDate
                            // If not, log a warning as this might cause display issues
                            if (exception.newStartTime) {
                                const startTimeDate = new Date(exception.newStartTime).toISOString().split('T')[0];
                                if (startTimeDate !== exceptionDate) {
                                    console.warn(`Warning: Exception newStartTime date (${startTimeDate}) differs from exceptionDate (${exceptionDate}). This may cause display issues.`);
                                }
                            }
                            
                            // Save the exception to the appointmentExceptions store
                            await indexedDbService.saveAppointmentException(exception);
                            console.log('Saved exception to appointmentExceptions store:', exception);
                            
                            // Verify that the exception was saved
                            const savedException = await indexedDbService.getAppointmentException(exception.id);
                            console.log('Verified saved exception:', savedException);
                            
                            // Use the API client to create the exception
                            // This will be intercepted in offline mode and added to pending changes
                            try {
                                console.log('Making API call to create exception (will be handled by offline interceptor)');
                                await apiClient.post('/api/appointmentexceptions', {
                                    appointmentId: id,
                                    exceptionDate: exceptionDate,
                                    isDeleted: false,
                                    newStartTime: newStartTime,
                                    newEndTime: newEndTime,
                                    newTitle: appointment.title,
                                    newDescription: appointment.description,
                                    newAddress: appointment.address,
                                    newIsAllDay: appointment.isAllDay
                                });
                            } catch (error: any) {
                                // This error is expected in offline mode
                                if (error.isOfflineError) {
                                    console.log('Offline error caught as expected, exception creation request queued for processing when online');
                                } else {
                                    console.error('Unexpected error making API call for exception creation:', error);
                                }
                            }
                            
                            console.log('Added pending change for exception creation');
                            
                            // Instead of manually adding a pending change, use the API client
                            // which will handle the offline case and normalize dates
                            try {
                                console.log('Making API call to update recurring instance (will be handled by offline interceptor)');
                                await apiClient.put(`${this.baseUrl}/recurring/instance/${id}`, appointment);
                            } catch (error: any) {
                                // This error is expected in offline mode
                                if (error.isOfflineError) {
                                    console.log('Offline error caught as expected, request queued for processing when online');
                                } else {
                                    console.error('Unexpected error making API call:', error);
                                }
                            }
                            
                            // Return a mock successful response with the updated appointment
                            // Create a merged appointment that includes the exception changes
                            const updatedAppointment: Appointment = {
                                ...baseAppointment,
                                id: `${baseAppointment.id}_${exceptionDate}`, // Generate a unique ID for this instance
                                startTime: appointment.startTime,
                                endTime: appointment.endTime,
                                title: appointment.title,
                                description: appointment.description,
                                address: appointment.address,
                                isAllDay: appointment.isAllDay,
                                recurringAppointmentId: baseAppointment.id,
                                isException: true,
                                exceptionDate: exceptionDate,
                                // Clear the recurrenceRule for the instance
                                recurrenceRule: undefined
                            };
                            
                            return {
                                success: true,
                                appointment: updatedAppointment
                            };
                        } catch (error) {
                            console.error('Error creating or saving exception:', error);
                            return {
                                success: false,
                                errorMessage: `Error creating or saving exception: ${error}`
                            };
                        }
                    } else {
                        console.error('Cannot update recurring instance without originalDate');
                        return {
                            success: false,
                            errorMessage: 'Cannot update recurring instance without originalDate'
                        };
                    }
                } else {
                    console.error('Base recurring appointment not found in IndexedDB:', id);
                    return {
                        success: false,
                        errorMessage: 'Base recurring appointment not found in offline storage'
                    };
                }
            }
            
            // Online mode - make the API call
            // Server returns AppointmentResult with Success, Appointment, ErrorMessage
            const response = await apiClient.put<AppointmentResult>(`${this.baseUrl}/recurring/instance/${id}`, appointment);

            // Invalidate appointment caches
            this.invalidateAppointmentCaches();

            // Return the result directly since server returns AppointmentResult format
            return response.data;
        } catch (error: any) {
            // Check if this is our custom offline error
            if (error.isOfflineError) {
                console.log('Offline error caught in updateRecurringInstance');
                // Return a mock successful response
                return {
                    success: true,
                    appointment: appointment as unknown as Appointment
                };
            }
            
            console.error('Error updating recurring instance:', error);
            return {
                success: false,
                errorMessage: extractApiErrorMessage(error, 'An error occurred while updating the recurring instance')
            };
        }
    }

    async deleteAppointment(id: string, instanceDate?: Date): Promise<void> {
        try {
            // Check if we're offline
            const isOffline = !offlineService.getOnlineStatus();
            
            // Determine the URL based on whether this is a recurring instance
            const url = instanceDate 
                ? `${this.baseUrl}/${id}?instanceDate=${this.ensureUTCFormat(instanceDate)}`
                : `${this.baseUrl}/${id}`;
            
            // Use the instance date or the ID as the entity ID for the pending change
            const entityId = instanceDate 
                ? `${id}_${this.ensureUTCFormat(instanceDate).split('T')[0]}`
                : id;
            
            if (isOffline) {
                console.log('Handling appointment deletion in offline mode');
                
                // If this is a recurring instance, we need to handle it differently
                if (instanceDate) {
                    console.log(`Adding recurring instance deletion to IndexedDB: ${id} on ${this.ensureUTCFormat(instanceDate)}`);
                    
                    // For recurring instances, we need to create an exception in IndexedDB
                    // so that the UI knows not to display this instance
                    const exceptionDate = this.ensureUTCFormat(instanceDate).split('T')[0];
                    console.log(`Creating deletion exception for date: ${exceptionDate}`);
                    
                    // Get the original appointment to copy its properties
                    const originalAppointment = await indexedDbService.getAppointment(id);
                    
                    if (originalAppointment) {
                        console.log('Found original appointment for deletion:', {
                            id: originalAppointment.id,
                            title: originalAppointment.title,
                            startTime: originalAppointment.startTime,
                            exceptionDate: exceptionDate
                        });
                        
                        try {
                            // Create a deletion exception
                            const exception: AppointmentException = {
                                id: `${id}_${exceptionDate}_delete`,
                                appointmentId: id,
                                exceptionDate: exceptionDate,
                                isDeleted: true,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                            };
                            
                            console.log('Created deletion exception object:', exception);
                            
                            // Save the exception to the appointmentExceptions store
                            await indexedDbService.saveAppointmentException(exception);
                            console.log('Saved deletion exception to appointmentExceptions store:', exception);
                            
                            // Verify that the exception was saved
                            const savedException = await indexedDbService.getAppointmentException(exception.id);
                            console.log('Verified saved deletion exception:', savedException);
                    
                    // Also try to delete any existing instance of this appointment
                    try {
                        // Find any existing instances of this appointment on this date
                        const allAppointments = await indexedDbService.getAllAppointments();
                        const matchingInstances = allAppointments.filter((a: Appointment) => 
                            a.recurringAppointmentId === id && 
                                    this.isSameDay(a.startTime, this.ensureUTCFormat(instanceDate))
                        );
                        
                        console.log(`Found ${matchingInstances.length} existing instances to delete`);
                        
                        // Delete each matching instance
                        for (const instance of matchingInstances) {
                            console.log(`Deleting existing instance: ${instance.id}`);
                            await indexedDbService.deleteAppointment(instance.id);
                        }
                    } catch (err) {
                        console.error('Error deleting existing instances:', err);
                            }
                            
                            // Use the API client to delete the appointment
                            // This will be intercepted in offline mode and added to pending changes
                            try {
                                console.log('Making API call to delete appointment (will be handled by offline interceptor)');
                                await apiClient.delete(url);
                            } catch (error: any) {
                                // This error is expected in offline mode
                                if (error.isOfflineError) {
                                    console.log('Offline error caught as expected, deletion request queued for processing when online');
                                } else {
                                    console.error('Unexpected error making API call for deletion:', error);
                                }
                            }
                        } catch (error) {
                            console.error('Error creating or saving deletion exception:', error);
                        }
                    } else {
                        console.error('Original appointment not found for deletion:', id);
                    }
                } else {
                    // For regular appointments, just delete from IndexedDB
                    await indexedDbService.deleteAppointment(id);
                    
                    // Use the API client to delete the appointment
                    // This will be intercepted in offline mode and added to pending changes
                    try {
                        console.log('Making API call to delete appointment (will be handled by offline interceptor)');
                        await apiClient.delete(url);
                    } catch (error: any) {
                        // This error is expected in offline mode
                        if (error.isOfflineError) {
                            console.log('Offline error caught as expected, deletion request queued for processing when online');
                        } else {
                            console.error('Unexpected error making API call for deletion:', error);
                        }
                    }
                }
                
                console.log(`Added pending change for appointment deletion: ${entityId}`);
                
                // Invalidate appointment caches
                this.invalidateAppointmentCaches();
                cacheService.remove(`appointment:${id}`);
                
                return;
            }
            
            // Online mode - make the API call
            await apiClient.delete(url);
            
            // Invalidate appointment caches
            this.invalidateAppointmentCaches();
            cacheService.remove(`appointment:${id}`);
        } catch (error: any) {
            // Check if this is our custom offline error
            if (error.isOfflineError) {
                console.log('Offline error caught in deleteAppointment');
                return;
            }
            
            console.error('Error deleting appointment:', error);
            throw error;
        }
    }

    async deleteRecurringSeries(id: string, deleteOption: 'ThisInstance' | 'AllInstances'): Promise<void> {
        try {
            await apiClient.delete(`${this.baseUrl}/recurring/series/${id}?deleteOption=${deleteOption}`);
            
            // Invalidate appointment caches
            this.invalidateAppointmentCaches();
        } catch (error) {
            console.error('Error deleting recurring appointment:', error);
            throw error;
        }
    }
    
    // Helper method to invalidate all appointment-related caches
    private invalidateAppointmentCaches(): void {
        cacheService.removeByPrefix('appointments:');
        cacheService.remove('appointments:recent-activity');
    }

    // Helper method to check if two dates are on the same day
    private isSameDay(date1String: string, date2String: string): boolean {
        const date1 = new Date(date1String);
        const date2 = new Date(date2String);
        
        return date1.getFullYear() === date2.getFullYear() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getDate() === date2.getDate();
    }

    async getAppointmentsForBusiness(
        businessId: string,
        startDate: Date,
        endDate: Date
    ): Promise<Appointment[]> {
        try {
            const isOnline = offlineService.getOnlineStatus();
            
            if (isOnline) {
                // If online, try to fetch from the server
                const startDateParam = startDate.toISOString();
                const endDateParam = endDate.toISOString();
                
                try {
                    // Use apiClient instead of fetch to ensure authentication headers are included
                    const response = await apiClient.get(
                        `/appointments/business/${businessId}?startDate=${startDateParam}&endDate=${endDateParam}`
                    );
                    
                    const appointments = response.data;
                    
                    // Note: IndexedDB caching is now handled by the caller
                    // using mergeCachedAppointments() to preserve data outside the fetched range
                    
                    return appointments;
                } catch (error) {
                    console.error('Error fetching appointments for business:', error);
                    // Fall back to IndexedDB
                }
            }
            
            // If offline or server request failed, get from IndexedDB and generate recurring instances
            return this.getAppointmentsFromIndexedDb(businessId, startDate, endDate);
        } catch (error) {
            console.error('Error in getAppointmentsForBusiness:', error);
            throw error;
        }
    }

    private async getAppointmentsFromIndexedDb(
        businessId: string,
        startDate: Date,
        endDate: Date
    ): Promise<Appointment[]> {
        console.log(`Getting appointments from IndexedDB for business ${businessId}`, {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
        });
        
        try {
            // Get all appointments from IndexedDB
            const allAppointments = await indexedDbService.getAllAppointments();
            console.log(`Retrieved ${allAppointments.length} total appointments from IndexedDB`);
            
            // Log all appointments to help diagnose issues
            allAppointments.forEach(appointment => {
                console.log('Appointment in IndexedDB:', {
                    id: appointment.id,
                    title: appointment.title,
                    businessId: appointment.businessId,
                    recurringAppointmentId: appointment.recurringAppointmentId,
                    recurrenceRule: appointment.recurrenceRule,
                    isException: appointment.isException,
                    startTime: appointment.startTime,
                    endTime: appointment.endTime
                });
            });
            
        // Filter for this business
        const businessAppointments = allAppointments.filter(a => a.businessId === businessId);
        console.log(`Found ${businessAppointments.length} appointments for business ${businessId}`);
        
        // Debug: Log recurring appointment details
        const recurringInBusiness = businessAppointments.filter(a => a.recurrenceRule && a.recurrenceRule.trim() !== '');
        console.log(`DEBUG: Business has ${recurringInBusiness.length} appointments with recurrence rules:`, 
            recurringInBusiness.map(a => ({
                id: a.id,
                title: a.title,
                description: a.description,
                recurrenceRule: a.recurrenceRule,
                recurringAppointmentId: a.recurringAppointmentId,
                isBaseRecurring: !a.recurringAppointmentId,
                isInstance: !!a.recurringAppointmentId,
                startTime: a.startTime
            }))
        );
        
        // Check if these are base appointments or instances
        const baseRecurringCount = recurringInBusiness.filter(a => !a.recurringAppointmentId).length;
        const instanceCount = recurringInBusiness.filter(a => a.recurringAppointmentId).length;
        console.log(`DEBUG: Of ${recurringInBusiness.length} with recurrence rules: ${baseRecurringCount} are base, ${instanceCount} are instances`);
            
            // If we don't have any appointments for this business, check if we have any appointments at all
            if (businessAppointments.length === 0 && allAppointments.length > 0) {
                console.log('No appointments found for the specified business. Business IDs in database:', 
                    [...new Set(allAppointments.map(a => a.businessId))]);
                
                // If we have a base recurring appointment but it doesn't have a business ID,
                // let's use it anyway (this is a workaround for potential data issues)
                const baseRecurringAppointments = allAppointments.filter(
                    a => a.recurrenceRule && !a.recurringAppointmentId
                );
                
                if (baseRecurringAppointments.length > 0) {
                    console.log('Found base recurring appointments without matching business ID, using them anyway:', 
                        baseRecurringAppointments.map(a => ({ id: a.id, title: a.title })));
                    
                    // Add the business ID to these appointments
                    baseRecurringAppointments.forEach(a => a.businessId = businessId);
                    
                    // Use these appointments instead
                    return this.processAppointmentsForOffline(baseRecurringAppointments, startDate, endDate);
                }
            }
            
            return this.processAppointmentsForOffline(businessAppointments, startDate, endDate);
        } catch (error) {
            console.error('Error retrieving appointments from IndexedDB:', error);
            return [];
        }
    }
    
    /**
     * Process appointments for offline mode, filtering by date range and generating recurring instances
     */
    private async processAppointmentsForOffline(
        appointments: Appointment[],
        startDate: Date,
        endDate: Date
    ): Promise<Appointment[]> {
        console.log(`[processAppointmentsForOffline] Processing ${appointments.length} appointments`);
        console.log(`[processAppointmentsForOffline] Sample appointments:`, appointments.slice(0, 3).map(a => ({
            id: a.id,
            title: a.title,
            recurrenceRule: a.recurrenceRule,
            recurringAppointmentId: a.recurringAppointmentId
        })));
        
        // Debug: Check if any appointments have recurrenceRule before filtering
        const appointmentsWithRecurrenceRule = appointments.filter(a => a.recurrenceRule && a.recurrenceRule.trim() !== '');
        console.log(`[DEBUG] Appointments with recurrenceRule before filtering: ${appointmentsWithRecurrenceRule.length}`);
        if (appointmentsWithRecurrenceRule.length > 0) {
            console.log(`[DEBUG] First appointment with recurrenceRule:`, {
                id: appointmentsWithRecurrenceRule[0].id,
                recurrenceRule: appointmentsWithRecurrenceRule[0].recurrenceRule,
                recurringAppointmentId: appointmentsWithRecurrenceRule[0].recurringAppointmentId
            });
        }
        
        // Filter non-recurring appointments by date range
        const nonRecurringAppointments = appointments
            .filter((appointment: Appointment) => !appointment.recurrenceRule && !appointment.recurringAppointmentId)
            .filter((appointment: Appointment) => {
                const appointmentStart = new Date(appointment.startTime);
                const appointmentEnd = new Date(appointment.endTime);
                return appointmentStart <= endDate && appointmentEnd >= startDate;
            });
        
        console.log(`Found ${nonRecurringAppointments.length} non-recurring appointments in date range`);
        
        // Get base recurring appointments
        // Since the backend already expands recurring appointments into instances,
        // we need to identify base appointments by finding those where id === recurringAppointmentId
        // or those with a recurrenceRule but no recurringAppointmentId
        const appointmentsWithRecurrence = appointments.filter(
            (appointment: Appointment) => (appointment.recurrenceRule && 
                                          appointment.recurrenceRule.trim() !== '') ||
                                          (appointment.recurringAppointmentId && 
                                           appointment.recurringAppointmentId.trim() !== '')
        ).filter((appointment: Appointment) => {
            const appointmentStart = new Date(appointment.startTime);
            const appointmentEnd = new Date(appointment.endTime);
            return appointmentStart <= endDate && appointmentEnd >= startDate;
        });
        
        console.log(`[DEBUG] Total appointments with recurrence rules: ${appointmentsWithRecurrence.length}`);
        
        if (appointmentsWithRecurrence.length > 0) {
        console.log(`[DEBUG] First 3 appointments with recurrence rules:`, 
            appointmentsWithRecurrence.slice(0, 3).map(a => ({ 
                id: a.id, 
                title: a.title, 
                recurrenceRule: a.recurrenceRule,
                recurringAppointmentId: a.recurringAppointmentId,
                idEqualsRecurringId: a.id === a.recurringAppointmentId,
                hasRecurringId: !!a.recurringAppointmentId
            }))
        );
        
        // Debug: Check if any of the recurring instances have recurrenceRule
        const instancesWithRecurrenceRule = appointmentsWithRecurrence.filter(a => a.recurrenceRule && a.recurrenceRule.trim() !== '');
        console.log(`[DEBUG] Instances with recurrenceRule: ${instancesWithRecurrenceRule.length}`);
        if (instancesWithRecurrenceRule.length > 0) {
            console.log(`[DEBUG] First instance with recurrenceRule:`, {
                id: instancesWithRecurrenceRule[0].id,
                recurrenceRule: instancesWithRecurrenceRule[0].recurrenceRule,
                recurringAppointmentId: instancesWithRecurrenceRule[0].recurringAppointmentId
            });
        }
            
            // Check if any appointments have id === recurringAppointmentId
            const baseAppointments = appointmentsWithRecurrence.filter(a => a.id === a.recurringAppointmentId);
            console.log(`[DEBUG] Appointments where id === recurringAppointmentId: ${baseAppointments.length}`);
            
            // Check if any appointments have no recurringAppointmentId
            const appointmentsWithoutRecurringId = appointmentsWithRecurrence.filter(a => !a.recurringAppointmentId);
            console.log(`[DEBUG] Appointments without recurringAppointmentId: ${appointmentsWithoutRecurringId.length}`);
        }
        
        // The backend generates all instances with unique IDs and sets recurringAppointmentId to the base appointment ID
        // The backend only returns instances within the date range, not the base appointments
        // So we need to reconstruct base appointments from the instances
        const recurringAppointmentIds = new Set(
            appointmentsWithRecurrence
                .map(a => a.recurringAppointmentId)
                .filter(id => id !== undefined && id !== null)
        );
        
        console.log(`[DEBUG] Found ${recurringAppointmentIds.size} unique recurring appointment IDs`);
        
        // First, try to find base appointments that are in the appointments array
        const baseAppointmentsInArray = appointments.filter(
            (appointment: Appointment) => {
                const hasRecurrenceRule = appointment.recurrenceRule && 
                                          appointment.recurrenceRule.trim() !== '';
                
                if (!hasRecurrenceRule) return false;
                
                // It's a base appointment if its id is referenced by other appointments as their recurringAppointmentId
                const isBaseAppointment = recurringAppointmentIds.has(appointment.id);
                
                if (isBaseAppointment) {
                    console.log(`[DEBUG] Found base recurring appointment in array: ${appointment.id}`, {
                        title: appointment.title,
                        id: appointment.id
                    });
                }
                
                return isBaseAppointment;
            }
        );
        
        // Now, reconstruct base appointments from instances for IDs that aren't in the array
        const missingBaseAppointmentIds = Array.from(recurringAppointmentIds).filter(
            (id): id is string => id !== undefined && !appointments.some(a => a.id === id)
        );
        
        console.log(`[DEBUG] Missing ${missingBaseAppointmentIds.length} base appointments (need to reconstruct from instances)`);
        
        const reconstructedBaseAppointments: Appointment[] = [];
        for (const baseId of missingBaseAppointmentIds) {
            // Find the first instance with this recurringAppointmentId
            const firstInstance = appointmentsWithRecurrence.find(a => a.recurringAppointmentId === baseId);
            
            if (firstInstance) {
                // Create a base appointment by copying the instance and setting its id to the base id
                console.log(`[DEBUG] First instance for base ${baseId}:`, {
                    id: firstInstance.id,
                    recurringAppointmentId: firstInstance.recurringAppointmentId,
                    recurrenceRule: firstInstance.recurrenceRule,
                    title: firstInstance.title
                });
                
                const { recurringAppointmentId, ...instanceWithoutRecurringId } = firstInstance;
                console.log(`[DEBUG] Instance without recurringAppointmentId:`, {
                    recurrenceRule: instanceWithoutRecurringId.recurrenceRule,
                    title: instanceWithoutRecurringId.title
                });
                
                const baseAppointment: Appointment = {
                    ...instanceWithoutRecurringId,
                    id: baseId,
                    // Use original dates if available, otherwise use instance dates
                    startTime: firstInstance.originalStartTime || firstInstance.startTime,
                    endTime: firstInstance.originalEndTime || firstInstance.endTime,
                    // Preserve the recurrence rule from the instance (explicitly set to ensure it's not lost)
                    recurrenceRule: firstInstance.recurrenceRule || instanceWithoutRecurringId.recurrenceRule
                };
                
                console.log(`[DEBUG] Final base appointment:`, {
                    id: baseAppointment.id,
                    recurrenceRule: baseAppointment.recurrenceRule,
                    title: baseAppointment.title
                });
                
                reconstructedBaseAppointments.push(baseAppointment);
                console.log(`[DEBUG] Reconstructed base recurring appointment: ${baseId} from instance ${firstInstance.id}`, {
                    title: baseAppointment.title,
                    recurrenceRule: baseAppointment.recurrenceRule,
                    startTime: baseAppointment.startTime,
                    endTime: baseAppointment.endTime,
                    originalStartTime: firstInstance.originalStartTime,
                    originalEndTime: firstInstance.originalEndTime,
                    instanceStartTime: firstInstance.startTime,
                    instanceEndTime: firstInstance.endTime
                });
            }
        }
        
        // Combine base appointments found in array and reconstructed ones
        const recurringAppointments = [...baseAppointmentsInArray, ...reconstructedBaseAppointments];
        
        console.log(`Found ${recurringAppointments.length} recurring appointments`, 
            recurringAppointments.map(a => ({ 
                id: a.id, 
                title: a.title, 
                recurrenceRule: a.recurrenceRule,
                recurringAppointmentId: a.recurringAppointmentId
            }))
        );
        
        // Log appointments that have empty recurrence rules for debugging
        const emptyRecurrenceRules = appointments.filter(
            (appointment: Appointment) => appointment.recurrenceRule === ''
        );
        if (emptyRecurrenceRules.length > 0) {
            console.log(`Note: Found ${emptyRecurrenceRules.length} appointments with empty recurrenceRule strings (these are non-recurring)`);
        }
        
        // First, check if we have recurring instances that already have recurrenceRule
        // These are instances generated by the backend SQL CTE and can be used directly
        const allExistingRecurringInstances = appointmentsWithRecurrence.filter(
            (appointment: Appointment) => appointment.recurringAppointmentId &&
                                         appointment.recurrenceRule &&
                                         appointment.recurrenceRule.trim() !== ''
        );

        // Track which base appointments already have instances from the API
        // so we don't regenerate them client-side
        const baseIdsWithExistingInstances = new Set(
            allExistingRecurringInstances.map(a => a.recurringAppointmentId).filter(Boolean)
        );

        console.log(`Found ${allExistingRecurringInstances.length} existing recurring instances for ${baseIdsWithExistingInstances.size} base appointments`);

        // Use ALL existing instances (not just one per parent) - the backend returns
        // one instance per occurrence date and we need to keep all of them
        const recurringInstances: Appointment[] = [...allExistingRecurringInstances];

        for (const appointment of recurringAppointments) {
            // Check if we already have existing instances for this base appointment
            if (baseIdsWithExistingInstances.has(appointment.id)) {
                console.log(`Skipping generation for appointment ${appointment.id} - using existing API instances`);
                continue;
            }
            
            try {
                console.log(`Processing recurring appointment: ${appointment.id}`, {
                    title: appointment.title,
                    startTime: appointment.startTime,
                    recurrenceRule: appointment.recurrenceRule
                });
                
                // Get exceptions for this appointment from the appointmentExceptions store
                const exceptions = await indexedDbService.getAppointmentExceptions(appointment.id);
                console.log(`Retrieved ${exceptions.length} exceptions for appointment ${appointment.id}`);
                
                // Generate instances
                const instances = RecurrenceGenerator.generateRecurringInstances(
                    appointment,
                    startDate,
                    endDate,
                    exceptions
                );
                
                console.log(`Generated ${instances.length} instances for appointment ${appointment.id}`);
                recurringInstances.push(...instances);
            } catch (error) {
                console.error(`Error generating recurring instances for appointment ${appointment.id}:`, error);
            }
        }
        
        // Combine non-recurring appointments and recurring instances
        const allFilteredAppointments = [...nonRecurringAppointments, ...recurringInstances];
        console.log(`Returning ${allFilteredAppointments.length} total appointments (${nonRecurringAppointments.length} non-recurring + ${allExistingRecurringInstances.length} existing recurring instances + ${recurringInstances.length - allExistingRecurringInstances.length} generated recurring instances)`);
        
        return allFilteredAppointments;
    }

    async createRecurringInstanceException(
        recurringAppointmentId: string,
        instanceDate: string,
        changes: Partial<Appointment>
    ): Promise<AppointmentException> {
        // Ensure the start and end times are in UTC format with Z suffix
        let startTime = changes.startTime;
        let endTime = changes.endTime;
        
        if (startTime && !startTime.endsWith('Z')) {
            startTime = `${startTime}Z`;
            console.log(`Corrected startTime to UTC format: ${startTime}`);
        }
        
        if (endTime && !endTime.endsWith('Z')) {
            endTime = `${endTime}Z`;
            console.log(`Corrected endTime to UTC format: ${endTime}`);
        }
        
        // Create the exception
        const exception: Omit<AppointmentException, 'id' | 'createdAt' | 'updatedAt'> = {
            appointmentId: recurringAppointmentId,
            exceptionDate: instanceDate,
            isDeleted: false,
            newStartTime: startTime,
            newEndTime: endTime,
            newTitle: changes.title,
            newDescription: changes.description,
            newAddress: changes.address,
            newIsAllDay: changes.isAllDay
        };
        
        console.log('Creating appointment exception with times:', {
            originalStartTime: changes.startTime,
            correctedStartTime: startTime,
            originalEndTime: changes.endTime,
            correctedEndTime: endTime,
            timeZoneId: changes.timeZoneId
        });
        
        // Use the appointment exception service to create the exception
        return await appointmentExceptionService.createException(exception);
    }

    async deleteRecurringInstance(
        recurringAppointmentId: string,
        instanceDate: string
    ): Promise<AppointmentException> {
        // Create a deletion exception
        const exception: Omit<AppointmentException, 'id' | 'createdAt' | 'updatedAt'> = {
            appointmentId: recurringAppointmentId,
            exceptionDate: instanceDate,
            isDeleted: true
        };
        
        // Use the appointment exception service to create the exception
        return await appointmentExceptionService.createException(exception);
    }

    /**
     * Ensures a date string is properly formatted in UTC format with Z suffix
     */
    private ensureUTCFormat(dateTimeString: string | Date): string {
        if (!dateTimeString) return '';
        
        try {
            // If it's a string but doesn't end with Z, add it to ensure UTC interpretation
            if (typeof dateTimeString === 'string') {
                if (dateTimeString.endsWith('Z')) {
                    return dateTimeString; // Already in UTC format
                } else {
                    // Try parsing it as a date and convert to ISO string
                    const date = new Date(dateTimeString);
                    if (!isNaN(date.getTime())) {
                        const utcString = date.toISOString();
                        console.log(`Converted string date to UTC: ${dateTimeString} -> ${utcString}`);
                        return utcString;
                    } else {
                        // If it can't be parsed as a date, just add Z
                        const utcString = `${dateTimeString}Z`;
                        console.log(`Added Z to string date: ${dateTimeString} -> ${utcString}`);
                        return utcString;
                    }
                }
            }
            
            // If it's a Date object, convert to ISO string
            if (dateTimeString instanceof Date) {
                const utcString = dateTimeString.toISOString();
                console.log(`Converted Date to UTC: ${dateTimeString.toString()} -> ${utcString}`);
                return utcString;
            }
            
            // Fallback - try to handle it as best we can
            console.warn(`Unexpected date format: ${dateTimeString}`);
            return String(dateTimeString);
        } catch (error) {
            console.error(`Error converting date to UTC: ${error}`);
            return typeof dateTimeString === 'string' ? dateTimeString : '';
        }
    }

    /**
     * Ensures consistent timezone handling for appointment data
     * This is especially important when receiving data from SignalR
     */
    public normalizeAppointmentTimezone(appointment: Appointment): Appointment {
        if (!appointment) return appointment;
        
        try {
            // Only process if we have timestamps and a timezone
            if (appointment.startTime && appointment.endTime && appointment.timeZoneId) {
                console.log('Normalizing appointment timezone:', {
                    id: appointment.id,
                    timeZone: appointment.timeZoneId,
                    startTime: appointment.startTime,
                    endTime: appointment.endTime,
                    recurringAppointmentId: appointment.recurringAppointmentId,
                    isException: appointment.isException
                });

                // Clone the appointment to avoid mutating the original
                const normalized = { ...appointment };
                
                // Ensure times are in proper UTC format with Z suffix
                if (normalized.startTime && !normalized.startTime.endsWith('Z')) {
                    normalized.startTime = `${normalized.startTime}Z`;
                    console.log(`Added Z suffix to startTime: ${normalized.startTime}`);
                }
                
                if (normalized.endTime && !normalized.endTime.endsWith('Z')) {
                    normalized.endTime = `${normalized.endTime}Z`;
                    console.log(`Added Z suffix to endTime: ${normalized.endTime}`);
                }
                
                // Get browser's timezone
                const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                
                // Always handle timezone properly, especially for the appointment that initiated the change
                // The issue is that the client which initiated the change needs to see 
                // the correct local time, not the UTC time it submitted
                
                console.log('Timezone handling:', {
                    appointmentTimeZone: appointment.timeZoneId,
                    browserTimeZone: browserTimeZone,
                    isSignalRUpdate: true
                });
                
                // For recurring instances or exceptions, we need special handling
                if ((appointment.isException || appointment.recurringAppointmentId) && 
                    appointment.timeZoneId) {
                    console.log('Processing recurring instance or exception with timezone info:', {
                        id: appointment.id,
                        isException: appointment.isException,
                        recurringAppointmentId: appointment.recurringAppointmentId,
                        timeZoneId: appointment.timeZoneId
                    });
                    
                    // Preserve the timezone information for proper display in the calendar
                    // The FullCalendar component will use this timezone to convert the UTC time for display
                    normalized.timeZoneId = appointment.timeZoneId;
                }
                
                // Log the final normalized appointment
                console.log('Final normalized appointment:', {
                    id: normalized.id,
                    startTime: normalized.startTime,
                    endTime: normalized.endTime,
                    timeZoneId: normalized.timeZoneId,
                    isException: normalized.isException,
                    recurringAppointmentId: normalized.recurringAppointmentId
                });
                
                return normalized;
            }
        } catch (error) {
            console.error('Error normalizing appointment timezone:', error);
        }
        
        return appointment;
    }
}

export const appointmentService = AppointmentService.getInstance(); 